import { useMemo } from 'react';
import moment from 'moment';
import { RentalEvent } from '../../../services/models/rental';
import { StationEvent } from '../../../services/models/stationEvent';
import { EVENT_TYPE_LABELS, EVENT_TYPE_COLORS, STATUS_BADGE } from '../../StationOperations/components/eventConstants';
import { getRentalEventActorName, getRentalEventCauseLabel, getRentalEventDetailLabel, getRentalStatusInfo } from '../utils/rentalUtils';

// One merged row: either a rental status transition (audit trail) or a station
// event (keypad code, lock open, hook state) tied to the rental.
interface HistoryRow {
  key: string;
  createdAt: string;
  dotClassName: string;
  typeLabel: string;
  details: string;
  badge: { className: string; label: string };
}

const rentalEventToRow = (event: RentalEvent): HistoryRow => {
  const statusInfo = getRentalStatusInfo(event.newStatus);
  // Charge outcome first — it's the payload; the actor is already implied by
  // the cause label and stays readable via the hover title when truncated.
  const details = [getRentalEventDetailLabel(event), getRentalEventActorName(event)]
    .filter(Boolean)
    .join(' · ');
  return {
    key: `rental-${event.id}`,
    createdAt: event.createdAt,
    dotClassName: statusInfo.dotClassName,
    typeLabel: getRentalEventCauseLabel(event),
    details: details || '—',
    badge: { className: statusInfo.badgeClassName, label: statusInfo.label },
  };
};

const stationEventToRow = (event: StationEvent): HistoryRow => {
  const statusStyle = STATUS_BADGE[event.status] ?? STATUS_BADGE.ok;
  // A refused keypad attempt is recorded as code_used/error — label it as such
  // (same convention as the Operations console).
  const typeLabel = event.type === 'code_used' && event.status === 'error'
    ? 'Code refusé'
    : (EVENT_TYPE_LABELS[event.type] ?? event.type);
  // Lock number now folds into details (the standalone column was dropped for width).
  const details = [event.lock ? `Lock ${event.lock.position + 1}` : null, event.details]
    .filter(Boolean)
    .join(' · ');
  return {
    key: `station-${event.id}`,
    createdAt: event.createdAt,
    dotClassName: EVENT_TYPE_COLORS[event.type] ?? 'bg-neutral-40',
    typeLabel,
    details: details || '—',
    badge: { className: `${statusStyle.bg} ${statusStyle.text}`, label: statusStyle.label },
  };
};

interface RentalHistoryTableProps {
  events: RentalEvent[];
  stationEvents: StationEvent[];
}

const RentalHistoryTable: React.FC<RentalHistoryTableProps> = ({ events, stationEvents }) => {
  const rows = useMemo(() => {
    return [
      ...events.map(rentalEventToRow),
      ...stationEvents.map(stationEventToRow),
    ].sort((a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime());
  }, [events, stationEvents]);

  if (rows.length === 0) return null;

  return (
    <div className="flex flex-col gap-2 pt-4 border-t border-neutral-10">
      <span className="typo-small text-neutral-50">Historique</span>
      <div className="rounded-lg outline outline-1 outline-offset-[-1px] outline-neutral-10 overflow-hidden">
        {/* Horizontally scrollable so all columns stay reachable on narrow screens */}
        <div className="overflow-x-auto">
          <div className="min-w-[500px]">
            {/* Column headers */}
            <div className="flex items-center px-4 py-2 border-b border-neutral-10 bg-white">
              <span className="w-[120px] flex-shrink-0 typo-small tracking-[0.03em] !font-bold uppercase text-neutral-40">Horodatage</span>
              <span className="w-44 flex-shrink-0 typo-small tracking-[0.03em] !font-bold uppercase text-neutral-40">Événement</span>
              <span className="flex-grow typo-small tracking-[0.03em] !font-bold uppercase text-neutral-40">Détails</span>
              <span className="w-[88px] flex-shrink-0 typo-small tracking-[0.03em] !font-bold uppercase text-neutral-40 text-right">Statut</span>
            </div>

            {/* Rows — chronological, oldest first */}
            <div className="max-h-[280px] overflow-y-auto">
              {rows.map((row) => (
                <div key={row.key} className="flex items-start px-4 py-2 border-b border-neutral-5 last:border-b-0 hover:bg-neutral-5 transition-colors">
                  <span className="w-[120px] flex-shrink-0 typo-caption text-neutral-60 whitespace-nowrap">
                    {moment.utc(row.createdAt).local().format('DD/MM/YYYY HH:mm')}
                  </span>
                  <div className="w-44 flex-shrink-0 flex items-center gap-2 pr-2">
                    <span className={`size-1.5 rounded-[3px] flex-shrink-0 ${row.dotClassName}`} />
                    <span className="typo-small text-neutral truncate">{row.typeLabel}</span>
                  </div>
                  <span className="flex-grow typo-small text-neutral-60 leading-tight line-clamp-2 pr-2" title={row.details !== '—' ? row.details : undefined}>
                    {row.details}
                  </span>
                  <span className="w-[88px] flex-shrink-0 flex justify-end">
                    <span className={`inline-flex items-center px-2 py-0.5 rounded-md typo-caption font-medium whitespace-nowrap ${row.badge.className}`}>
                      {row.badge.label}
                    </span>
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RentalHistoryTable;
